'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  context?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.error(`[ErrorBoundary${this.props.context ? ` ${this.props.context}` : ''}]`, error, errorInfo);
    }
    this.props.onError?.(error, errorInfo);
  }

  private reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div
          role="alert"
          className="rounded-[var(--k-radius-lg)] border border-[var(--k-color-danger)]/30 bg-[var(--k-color-danger-soft)] p-4"
        >
          <h3 className="text-sm font-semibold text-[var(--k-color-danger-strong)]">
            Ein Fehler ist aufgetreten
          </h3>
          <p className="mt-1 text-sm text-[var(--k-color-text-secondary)]">
            Bitte lade die Seite neu oder versuche es erneut.
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="mt-3 inline-flex items-center rounded-[var(--k-radius-md)] border border-[var(--k-color-danger)]/40 bg-[var(--k-color-surface)] px-3 py-1.5 text-xs font-semibold text-[var(--k-color-danger-strong)] transition hover:border-[var(--k-color-danger)]"
          >
            Erneut versuchen
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
